import React, { useState, useEffect } from 'react';
import { getAddons, createAddon, updateAddon, deleteAddon, uploadImage } from '../../services/api';
import { Addon, Option } from '../../types';
import { PlusIcon, EditIcon, TrashIcon, UploadIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';

const AddonsManagement: React.FC = () => {
  const [addons, setAddons] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingAddon, setEditingAddon] = useState<Addon | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    images: [] as string[],
    options: [] as Option[],
    option_label: ''
  });
  const [newOption, setNewOption] = useState({ label: '', image: '' });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchAddons();
  }, []);

  const fetchAddons = async () => {
    try {
      const data = await getAddons();
      setAddons(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching addons:', error);
      setAddons([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const addonData = {
        addon_id: editingAddon ? editingAddon.addon_id : `addon_${Date.now()}`,
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price) * 100,
        images: formData.images,
        options: formData.options,
        option_label: formData.option_label || undefined
      };

      if (editingAddon) {
        // For updates, only send fields that can be updated (exclude addon_id)
        const updateData = {
          name: addonData.name,
          description: addonData.description,
          price: addonData.price,
          images: addonData.images,
          options: addonData.options,
          option_label: addonData.option_label
        };
        await updateAddon(editingAddon.addon_id, updateData);
      } else {
        await createAddon(addonData);
      }

      fetchAddons();
      setShowForm(false);
      setEditingAddon(null);
      setFormData({ name: '', description: '', price: '', images: [], options: [], option_label: '' });
      setNewOption({ label: '', image: '' });
    } catch (error) {
      console.error('Error saving addon:', error);
      setError('Failed to save addon');
    }
  };

  const handleEdit = (addon: Addon) => {
    setEditingAddon(addon);
    setFormData({
      name: addon.name,
      description: addon.description,
      // Convert from cents to dollars for editing
      price: addon.price ? (parseFloat(String(addon.price)) / 100).toString() : '0',
      images: addon.images || [],
      options: addon.options || [],
      option_label: addon.option_label || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (addonId: string) => {
    if (window.confirm('Are you sure you want to delete this addon?')) {
      try {
        await deleteAddon(addonId);
        fetchAddons();
      } catch (error) {
        console.error('Error deleting addon:', error);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingAddon(null);
    setFormData({ name: '', description: '', price: '', images: [], options: [], option_label: '' });
    setNewOption({ label: '', image: '' });
  };

  const handleAddOption = () => {
    if (newOption.label.trim()) {
      const option: any = {
        label: newOption.label.trim(),
      };
      if (newOption.image) {
        option.image = newOption.image;
      }
      setFormData({ ...formData, options: [...formData.options, option] });
      setNewOption({ label: '', image: '' });
    }
  };

  const handleRemoveOption = (index: number) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index)
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImage(true);
    try {
      // Generate a temporary addon ID for uploads, will be updated when addon is saved
      const tempAddonId = editingAddon 
        ? editingAddon.addon_id 
        : formData.name.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now();
      
      console.log('Uploading addon images for addon ID:', tempAddonId);
      console.log('Files to upload:', Array.from(files).map(f => f.name));
      
      const uploadPromises = Array.from(files).map(file => 
        uploadImage(file, tempAddonId, 'addon-image', `Addon image for ${formData.name}`)
      );
      const results = await Promise.all(uploadPromises);
      
      console.log('Upload results:', results);
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...results.map(r => r.s3_url)]
      }));
    } catch (error: any) {
      console.error('Error uploading addon images:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Show more detailed error message
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to upload addon images';
      alert(`Upload failed: ${errorMessage}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const toggleRowExpanded = (addonId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(addonId)) {
        newSet.delete(addonId);
      } else {
        newSet.add(addonId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <h3 className="text-lg font-medium text-gray-900">Addons Management</h3>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Addon
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingAddon ? 'Edit Addon' : 'Add New Addon'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Addon Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                required
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Options Dropdown Label
              </label>
              <input
                type="text"
                value={formData.option_label}
                onChange={(e) => setFormData({ ...formData, option_label: e.target.value })}
                placeholder="e.g., 'Select Color', 'Choose Theme' (default: 'Select Option')"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Customize the label shown in the options dropdown</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Options (Themes, Colors, etc.)
              </label>
              <div className="space-y-3">
                {/* Add new option */}
                <div className="space-y-2 p-3 border border-gray-200 rounded-md bg-gray-50">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newOption.label}
                      onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                      placeholder="Option label (e.g., Red, Princess Theme)"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <select
                      value={newOption.image}
                      onChange={(e) => setNewOption({ ...newOption, image: e.target.value })}
                      className="w-48 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="">Use default (first image)</option>
                      {formData.images.map((image, index) => (
                        <option key={image} value={image}>
                          Image {index + 1}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleAddOption}
                      disabled={!newOption.label.trim()}
                      className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>
                </div>
                
                {/* Display existing options */}
                {formData.options.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">Current options:</div>
                    <div className="space-y-2">
                      {formData.options.map((option, index) => (
                        <div
                          key={index}
                          className="p-3 bg-gray-50 rounded-md border border-gray-200"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <span className="text-sm font-medium text-gray-900">
                                {option.label}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveOption(index)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                          
                          {/* Image selection for existing option */}
                          <div className="flex items-center space-x-2 mt-2">
                            <label className="text-xs text-gray-600">Option image:</label>
                            <select
                              value={option.image || ''}
                              onChange={(e) => {
                                const updatedOptions = [...formData.options];
                                updatedOptions[index] = {
                                  ...option,
                                  image: e.target.value || undefined
                                };
                                setFormData({ ...formData, options: updatedOptions });
                              }}
                              className="flex-1 text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="">Use default (first image)</option>
                              {formData.images.map((image, imgIndex) => (
                                <option key={image} value={image}>
                                  Image {imgIndex + 1}
                                </option>
                              ))}
                            </select>
                            {option.image && (
                              <img 
                                src={option.image} 
                                alt={`${option.label} option`}
                                className="h-8 w-8 object-cover rounded border border-gray-300"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Addon Images
              </label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Upload image(s):</label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                    />
                  </div>
                  {uploadingImage && (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-sm text-gray-600">Uploading...</span>
                    </div>
                  )}
                </div>
                
                {formData.images.length > 0 && (
                  <div className="mt-4">
                    <label className="block text-xs text-gray-600 mb-2">Current images:</label>
                    <div className="grid grid-cols-4 gap-2">
                      {formData.images.map((imageUrl, index) => (
                        <div key={`${imageUrl}-${index}`} className="relative group">
                          <img
                            src={imageUrl}
                            alt={`Addon image ${index + 1}`}
                            className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove image"
                          >
                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                {editingAddon ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {Array.isArray(addons) && addons.map((addon) => (
          <div key={addon.addon_id} className="bg-white rounded-lg shadow p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-shrink-0">
                {addon.images && addon.images.length > 0 ? (
                  <img
                    src={addon.images[0]}
                    alt={addon.name}
                    className="h-16 w-16 rounded-lg object-cover"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-lg bg-gray-200 flex items-center justify-center">
                    <PlusIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 truncate">{addon.name}</h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  ${addon.price ? (parseFloat(String(addon.price)) / 100).toFixed(2) : '0.00'}
                </p>
                {addon.options && addon.options.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {addon.options.slice(0, 2).map((option, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {option.label}
                      </span>
                    ))}
                    {addon.options.length > 2 && (
                      <span className="text-xs text-gray-500">+{addon.options.length - 2} more</span>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {addon.description && (
              <p className="text-sm text-gray-600 line-clamp-2 mb-3">{addon.description}</p>
            )}
            
            <div className="flex items-center justify-between pt-3 border-t border-gray-200">
              <button
                onClick={() => toggleRowExpanded(addon.addon_id)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center"
              >
                {expandedRows.has(addon.addon_id) ? (
                  <>
                    <ChevronDownIcon className="h-4 w-4 mr-1" />
                    Hide Details
                  </>
                ) : (
                  <>
                    <ChevronRightIcon className="h-4 w-4 mr-1" />
                    View Details
                  </>
                )}
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(addon)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                  title="Edit"
                >
                  <EditIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(addon.addon_id)}
                  className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded"
                  title="Delete"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            {expandedRows.has(addon.addon_id) && (
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                {addon.images && addon.images.length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 mb-2">Images ({addon.images.length})</h5>
                    <div className="grid grid-cols-4 gap-2">
                      {addon.images.map((img, idx) => (
                        <img key={idx} src={img} alt={`${addon.name} ${idx + 1}`} className="h-16 w-16 object-cover rounded-lg border" />
                      ))}
                    </div>
                  </div>
                )}
                {addon.options && addon.options.length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 mb-2">Available Options</h5>
                    <div className="space-y-1">
                      {addon.options.map((option, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                          <span className="text-gray-900">{option.label}</span>
                          {(option.priceModifier || option.price_modifier) && (
                            <span className="font-medium text-green-600">+${((parseFloat(String(option.priceModifier || option.price_modifier)) / 100)).toFixed(2)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {addon.option_label && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 mb-1">Options Label</h5>
                    <p className="text-xs text-gray-600">{addon.option_label}</p>
                  </div>
                )}
                <div>
                  <h5 className="text-xs font-semibold text-gray-700 mb-1">Addon ID</h5>
                  <p className="text-xs text-gray-500 font-mono break-all">{addon.addon_id}</p>
                </div>
              </div>
            )}
          </div>
        ))}
        {addons.length === 0 && (
          <div className="text-center py-12 text-gray-500 bg-white rounded-lg">
            No addons found
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Addon Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Options
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(addons) && addons.map((addon) => (
                <React.Fragment key={addon.addon_id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {addon.images && addon.images.length > 0 ? (
                      <img
                        src={addon.images[0]}
                        alt={addon.name}
                        className="h-12 w-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400 text-xs">No image</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{addon.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 line-clamp-2">
                      {addon.description}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${addon.price ? (parseFloat(String(addon.price)) / 100).toFixed(2) : '0.00'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {addon.options && addon.options.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {addon.options.map((option, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {option.label}
                            {(option.priceModifier || option.price_modifier) && (
                              <span className="ml-1 text-blue-600">+${((parseFloat(String(option.priceModifier || option.price_modifier)) / 100)).toFixed(2)}</span>
                            )}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">No options</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => toggleRowExpanded(addon.addon_id)}
                        className="text-gray-600 hover:text-gray-900"
                        title="View Details"
                      >
                        {expandedRows.has(addon.addon_id) ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(addon)}
                        className="text-gray-600 hover:text-gray-900"
                        title="Edit"
                      >
                        <EditIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(addon.addon_id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedRows.has(addon.addon_id) && (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 bg-gray-50">
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Full Description</h4>
                          <p className="text-sm text-gray-600">{addon.description}</p>
                        </div>
                        {addon.images && addon.images.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Images ({addon.images.length})</h4>
                            <div className="grid grid-cols-6 gap-2">
                              {addon.images.map((img, idx) => (
                                <img key={idx} src={img} alt={`${addon.name} ${idx + 1}`} className="h-20 w-20 object-cover rounded-lg border" />
                              ))}
                            </div>
                          </div>
                        )}
                        {addon.options && addon.options.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Available Options</h4>
                            <div className="space-y-2">
                              {addon.options.map((option, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                                  <span className="text-sm text-gray-900">{option.label}</span>
                                  {(option.priceModifier || option.price_modifier) && (
                                    <span className="text-sm font-medium text-green-600">+${((parseFloat(String(option.priceModifier || option.price_modifier)) / 100)).toFixed(2)}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {addon.option_label && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-1">Options Dropdown Label</h4>
                            <p className="text-sm text-gray-600">{addon.option_label}</p>
                          </div>
                        )}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-1">Addon ID</h4>
                          <p className="text-xs text-gray-500 font-mono">{addon.addon_id}</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        
        {addons.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No addons found
          </div>
        )}
      </div>
    </div>
  );
};

export default AddonsManagement;
